import {compact} from 'lodash';
import {useState} from 'react';

import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {fetchApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {ApiSeenByList, SeenByModel} from '../../../../../src/models/api/seenByModel';

export function isParticipantRemovable(tracker: VisualConversationTrackerModel) {
  return (
    !tracker.isOwner && !tracker.isUserOrDelegatedTeammate && !tracker.isInboxMember && !tracker.isReader
  );
}

interface UseSeenByParticipantsProps {
  isTeamConversation?: boolean;
  conversationUrl: string;
}
export function useSeenByParticipants(props: UseSeenByParticipantsProps) {
  const [seenBy, setSeenBy] = useState<AsyncData<ReadonlyArray<SeenByModel>>>({
    status: AsyncStatusesEnum.LOADING,
  });
  const [cancelSeenByFetchTokenSource, setCancelSeenByFetchTokenSource] = useState<
    CancelTokenSource | undefined
  >(undefined);

  const fetchSeenByAsync = async () => {
    if (!props.isTeamConversation) {
      return;
    }

    setSeenBy(asyncDataLoading());

    try {
      const path = `${props.conversationUrl}/seen_by`;
      const seenByData = await fetchApiAsync(ApiSeenByList, path, {
        cancelToken: cancelSeenByFetchTokenSource ? cancelSeenByFetchTokenSource.token : undefined,
      });

      setSeenBy(asyncDataLoaded(seenByData));
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      setSeenBy(asyncDataFailed(error));
    }
  };

  useInitialEffect(() => {
    const newCancelTokenSource = buildCancelTokenSource();
    setCancelSeenByFetchTokenSource(newCancelTokenSource);
    fetchSeenByAsync();
  });

  return {seenBy, fetchSeenByAsync};
}

export interface VisualConversationTrackerModel {
  readonly teammate: TeammateModel;
  readonly isUser?: boolean;
  readonly isUserOrDelegatedTeammate?: boolean;
  readonly isOwner?: boolean;
  readonly isInboxMember?: boolean;
  readonly isReader?: boolean;
  readonly hasSeenAll?: boolean;
  readonly hasSnoozed?: boolean;
  readonly hasArchived?: boolean;
  readonly hasMuted: boolean;
  readonly seenAt?: number;
}

export function computeInviteDropdownItems(
  sortedSubscribers: ReadonlyArray<VisualConversationTrackerModel>,
  sortedNonSubscribers: ReadonlyArray<VisualConversationTrackerModel>,
): ReadonlyArray<InviteDropdownItem> {
  // show empty text when there are non subscribers but no subscribers
  const subscribedEmptyText: InviteDropdownItem = {
    type: InviteDropdownItemTypesEnum.SUBSCRIBED_EMPTY_TEXT,
  };

  const subscriberItems = sortedSubscribers.map(
    (visualTracker): Subscriber => ({visualTracker, type: InviteDropdownItemTypesEnum.SUBSCRIBER}),
  );

  const subscriberContent = subscriberItems.length > 0 ? subscriberItems : [subscribedEmptyText];

  // subscribed header
  const subscribedHeader: InviteDropdownItem = {
    type: InviteDropdownItemTypesEnum.SUBSCRIBED_HEADER,
  };

  // not subscribed header
  const notSubscribedHeader: InviteDropdownItem = {
    type: InviteDropdownItemTypesEnum.NOT_SUBSCRIBED_HEADER,
  };

  const nonSubscriberItems = sortedNonSubscribers.map(
    (visualTracker): NonSubscriber => ({visualTracker, type: InviteDropdownItemTypesEnum.NON_SUBSCRIBER}),
  );

  return compact([
    subscriberItems.length > 0 && subscribedHeader,
    ...subscriberContent,
    sortedNonSubscribers.length > 0 && notSubscribedHeader,
    ...nonSubscriberItems,
  ]);
}

export enum InviteDropdownItemTypesEnum {
  SUBSCRIBED_HEADER = 'subscribed_header',
  NOT_SUBSCRIBED_HEADER = 'not_subscribed_header',
  SUBSCRIBED_EMPTY_TEXT = 'subscribed_empty_text',
  SUBSCRIBER = 'subscriber',
  NON_SUBSCRIBER = 'non_subscriber',
}
interface SubscribedHeader {
  type: InviteDropdownItemTypesEnum.SUBSCRIBED_HEADER;
}
interface NotSubscribedHeader {
  type: InviteDropdownItemTypesEnum.NOT_SUBSCRIBED_HEADER;
}
interface SubscribedEmptyText {
  type: InviteDropdownItemTypesEnum.SUBSCRIBED_EMPTY_TEXT;
}

export interface Subscriber {
  type: InviteDropdownItemTypesEnum.SUBSCRIBER;
  visualTracker: VisualConversationTrackerModel;
}

export interface NonSubscriber {
  type: InviteDropdownItemTypesEnum.NON_SUBSCRIBER;
  visualTracker: VisualConversationTrackerModel;
}

export type InviteDropdownItem =
  | SubscribedHeader
  | NotSubscribedHeader
  | SubscribedEmptyText
  | Subscriber
  | NonSubscriber;
